/**
 * Card entry for a real (non-demo) paid registration (§8.6). The card is tokenised in the browser by the
 * event's gateway and only the token goes to our server — there is no raw-card path. Cardknox: iFields
 * iframes (this page never sees the number or CVV). Authorize.net: Accept.js turns the card into an opaque
 * token before anything is sent. Both are unverified against a live vendor in this build (DV-002).
 */
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Lock } from 'lucide-react';
import { TextField } from '@/components/ui/Field';

/** The billing street and ZIP go to our server with the token, only so the card's address check can run; they are not stored. */
export interface CardBilling { street: string; zip: string }
export type CardTokenPayload =
  | { kind: 'cardknox'; token: string; cvv?: string; exp: string; name: string; billing: CardBilling }
  | { kind: 'authnet'; opaque_data: { dataDescriptor: string; dataValue: string }; name: string; billing: CardBilling };
export interface CardFormHandle { tokenize: () => Promise<CardTokenPayload> }

const IFIELDS_VERSION = '2.15.2302.0801';
const IFIELDS_JS = `https://cdn.cardknox.com/ifields/${IFIELDS_VERSION}/ifields.min.js`;
const IFIELD_HTM = `https://cdn.cardknox.com/ifields/${IFIELDS_VERSION}/ifield.htm`;
const ACCEPT_JS = { sandbox: 'https://jstest.authorize.net/v1/Accept.js', live: 'https://js.authorize.net/v1/Accept.js' };
const LABEL: Record<string, string> = { cardknox: 'Cardknox', authnet: 'Authorize.net' };

declare global {
  interface Window {
    setAccount?: (key: string, software: string, version: string) => void;
    getTokens?: (ok: () => void, err: (d: { xError?: string }) => void, timeoutMs: number) => void;
    setIfieldStyle?: (id: string, style: Record<string, string>) => void;
    Accept?: { dispatchData: (data: unknown, cb: (r: { messages: { resultCode: string; message: Array<{ code: string; text: string }> }; opaqueData?: { dataDescriptor: string; dataValue: string } }) => void) => void };
  }
}

// One load per script, shared by every card form. A second form that found the tag still loading used to go on at
// once, before the card company's script had run, so its key was never set and every card came back "Missing xKey".
const loading = new Map<string, Promise<void>>();
function loadScript(src: string): Promise<void> {
  let p = loading.get(src);
  if (!p) {
    p = new Promise<void>((resolve, reject) => {
      const s = document.createElement('script');
      s.src = src;
      s.async = true;
      s.onload = () => resolve();
      s.onerror = () => { loading.delete(src); s.remove(); reject(new Error('load')); };
      document.head.appendChild(s);
    });
    loading.set(src, p);
  }
  return p;
}

export const validExpiry = (v: string): string | null => {
  const m = /^(\d{2})\s*\/\s*(\d{2}|\d{4})$/.exec(v.trim());
  if (!m) return 'Expiry must be in MM/YY or MM/YYYY format';
  const mm = Number(m[1]);
  if (mm < 1 || mm > 12) return 'Expiry month must be 01–12';
  const yy = m[2]!.length === 4 ? Number(m[2]) : 2000 + Number(m[2]);
  const now = new Date();
  if (yy < now.getFullYear() || (yy === now.getFullYear() && mm < now.getMonth() + 1)) return 'Card has expired';
  return null;
};
export const validStreet = (v: string): string | null => {
  const t = v.trim();
  if (!t) return 'Billing address is required';
  if (t.length > 100) return 'Billing address must be 100 characters or fewer';
  return null;
};
export const validZip = (v: string): string | null => {
  const t = v.trim();
  if (!t) return 'ZIP / postal code is required';
  if (t.length < 3 || t.length > 10 || !/^[A-Za-z0-9][A-Za-z0-9 -]*$/.test(t)) return 'Enter a valid ZIP or postal code';
  return null;
};
const mmyy = (v: string) => {
  const m = /^(\d{2})\s*\/\s*(\d{2}|\d{4})$/.exec(v.trim())!;
  return `${m[1]}${m[2]!.slice(-2)}`;
};

function CardMarks() {
  return (
    <span className="inline-flex gap-1" role="img" aria-label="Visa, Mastercard, American Express and Discover accepted">
      <svg viewBox="0 0 36 24" className="h-6 w-9 rounded-[3px] border border-line" aria-hidden><rect width="36" height="24" fill="#1A1F71" /><text x="18" y="16" fill="#fff" fontSize="9" fontWeight="700" textAnchor="middle" fontFamily="sans-serif">VISA</text></svg>
      <svg viewBox="0 0 36 24" className="h-6 w-9 rounded-[3px] border border-line" aria-hidden><rect width="36" height="24" fill="#252525" /><circle cx="15" cy="12" r="6" fill="#EB001B" /><circle cx="21" cy="12" r="6" fill="#F79E1B" /><path d="M18 7.2a6 6 0 0 1 0 9.6a6 6 0 0 1 0-9.6z" fill="#FF5F00" /></svg>
      <svg viewBox="0 0 36 24" className="h-6 w-9 rounded-[3px] border border-line" aria-hidden><rect width="36" height="24" fill="#007BC1" /><text x="18" y="15.5" fill="#fff" fontSize="7" fontWeight="700" textAnchor="middle" fontFamily="sans-serif">AMEX</text></svg>
      <svg viewBox="0 0 36 24" className="h-6 w-9 rounded-[3px] border border-line" aria-hidden><rect width="36" height="24" fill="#fff" /><circle cx="22" cy="12" r="4" fill="#F76E11" /><text x="12" y="14.5" fill="#111" fontSize="5.5" fontWeight="700" textAnchor="middle" fontFamily="sans-serif">DISC</text></svg>
    </span>
  );
}

export const CardForm = forwardRef<CardFormHandle, {
  gateway: 'cardknox' | 'authnet'; publicKey: string; sandbox: boolean; onReady: (ready: boolean) => void;
  /** The booking flow: the same form with no payment company named on it. */
  unnamed?: boolean;
  /** The words over the fields, where "Card payment" would be wrong (a card saved for later). */
  heading?: string;
}>(
  function CardForm({ gateway, publicKey, sandbox, onReady, unnamed = false, heading = 'Card payment' }, ref) {
    const [ready, setReady] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [name, setName] = useState('');
    const [exp, setExp] = useState('');
    const [street, setStreet] = useState('');
    const [zip, setZip] = useState('');
    const [num, setNum] = useState('');
    const [cvv, setCvv] = useState('');
    const [errs, setErrs] = useState<Record<string, string>>({});
    /** A field's own message goes as soon as the guest edits it, not on the next submit. */
    const clear = (k: string) => setErrs((was) => (k in was ? Object.fromEntries(Object.entries(was).filter(([key]) => key !== k)) : was));
    const tokenInput = useRef<HTMLInputElement>(null);
    const cvvInput = useRef<HTMLInputElement>(null);
    /** The card company's script writes its errors here; without it the script fails silently and never answers. */
    const cardError = useRef<HTMLSpanElement>(null);

    useEffect(() => {
      let dead = false;
      const src = gateway === 'cardknox' ? IFIELDS_JS : sandbox ? ACCEPT_JS.sandbox : ACCEPT_JS.live;
      loadScript(src)
        .then(() => {
          if (dead) return;
          if (gateway === 'cardknox') {
            if (!window.setAccount || !publicKey) throw new Error('no account');
            window.setAccount(publicKey, 'BooklynRoomMapPro', '1.0');
            // the fields inside the card company's frames take our look: no box of their own, our font and colours (owner, 2026-10-18)
            const css = getComputedStyle(document.documentElement);
            const ink = css.getPropertyValue('--ink').trim(), surface = css.getPropertyValue('--surface').trim();
            const style = { border: 'none', outline: 'none', margin: '0', padding: '0 12px', width: '100%', height: '100%', 'box-sizing': 'border-box',
              'font-size': '14px', 'font-family': 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif', ...(ink ? { color: ink } : {}), ...(surface ? { background: surface } : { background: 'transparent' }) };
            window.setIfieldStyle?.('card-number', style);
            window.setIfieldStyle?.('cvv', style);
          }
          setReady(true);
          onReady(true);
        })
        .catch(() => !dead && setLoadError('Secure card form failed to load. Please refresh the page and try again.'));
      return () => {
        dead = true;
        onReady(false);
      };
    }, [gateway, publicKey, sandbox]); // eslint-disable-line react-hooks/exhaustive-deps

    useImperativeHandle(ref, () => ({
      tokenize: async () => {
        const e: Record<string, string> = {};
        if (!name.trim()) e.name = 'Cardholder name is required';
        const ex = validExpiry(exp);
        if (ex) e.exp = ex;
        const st = validStreet(street);
        if (st) e.street = st;
        const zp = validZip(zip);
        if (zp) e.zip = zp;
        const billing = { street: street.trim(), zip: zip.trim() };
        if (gateway === 'authnet') {
          const digits = num.replace(/\D/g, '');
          if (!luhn(digits) || digits.length < 12 || digits.length > 19) e.num = 'Card number is invalid — please check the digits';
          if (!/^\d{3,4}$/.test(cvv)) e.cvv = 'CVV must be 3 or 4 digits';
        }
        setErrs(e);
        if (Object.keys(e).length) throw new Error(Object.values(e)[0]);
        if (gateway === 'cardknox') {
          if (!window.getTokens) throw new Error('Secure card form failed to load. Please refresh the page and try again.');
          await new Promise<void>((resolve, reject) => {
            // the card company's own wait is 15 s; ours is a little longer so its answer, not ours, is what the guest reads
            const t = window.setTimeout(() => reject(new Error("We couldn't reach the card service. Please check your connection and try again.")), 18000);
            window.getTokens!(() => { window.clearTimeout(t); resolve(); }, () => { window.clearTimeout(t); reject(new Error(cardError.current?.textContent?.trim() || 'Could not read card. Please re-enter your card details.')); }, 15000);
          });
          const token = tokenInput.current?.value;
          if (!token) throw new Error('Could not read card. Please re-enter your card details.');
          const cvvToken = cvvInput.current?.value;
          return { kind: 'cardknox', token, ...(cvvToken ? { cvv: cvvToken } : {}), exp: mmyy(exp), name: name.trim(), billing };
        }
        const [loginId, clientKey] = publicKey.split('|');
        if (!window.Accept || !loginId || !clientKey) throw new Error('Secure card form failed to load. Please refresh the page and try again.');
        const m = mmyy(exp);
        const opaque = await new Promise<{ dataDescriptor: string; dataValue: string }>((resolve, reject) => {
          const t = window.setTimeout(() => reject(new Error('Card tokenization timed out. Please try again.')), 6000);
          window.Accept!.dispatchData({ authData: { clientKey, apiLoginID: loginId }, cardData: { cardNumber: num.replace(/\D/g, ''), month: m.slice(0, 2), year: m.slice(2), cardCode: cvv } }, (r) => {
            window.clearTimeout(t);
            if (r.messages.resultCode === 'Ok' && r.opaqueData) resolve(r.opaqueData);
            else reject(new Error(r.messages.message[0]?.text || 'Could not read card. Please re-enter your card details.'));
          });
        });
        setNum('');
        setCvv('');
        return { kind: 'authnet', opaque_data: opaque, name: name.trim(), billing };
      },
    }));

    return (
      <div className="flex flex-col gap-3 rounded-control bg-surface p-3 ring-1 ring-line" data-testid="card-form">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-ink"><Lock className="size-4" aria-hidden />{heading}</span>
          {!unnamed && LABEL[gateway] && <span className="rounded-chip bg-surface-2 px-2 py-0.5 text-[12px] text-ink-2 ring-1 ring-line">{LABEL[gateway]} · PCI-DSS Level 1</span>}
        </div>
        {loadError ? <p role="alert" className="text-sm text-st-danger">{loadError}</p> : !ready ? (
          <div className="flex flex-col gap-3" aria-busy="true"><div className="h-control skeleton rounded-control" /><div className="h-control skeleton rounded-control" /></div>
        ) : gateway === 'cardknox' ? (
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="flex flex-col gap-1.5 sm:col-span-2 text-[13px] font-medium text-ink-2">Card number
              <iframe title="Card number" data-ifields-id="card-number" data-ifields-placeholder="Card number" src={IFIELD_HTM} className="h-control w-full overflow-hidden rounded-control border-0 bg-surface ring-1 ring-input" />
            </label>
            <label className="flex flex-col gap-1.5 text-[13px] font-medium text-ink-2">CVV
              <iframe title="CVV" data-ifields-id="cvv" data-ifields-placeholder="CVV" src={IFIELD_HTM} className="h-control w-full overflow-hidden rounded-control border-0 bg-surface ring-1 ring-input" />
            </label>
            <input ref={tokenInput} data-ifields-id="card-number-token" name="xCardNum" type="hidden" />
            <input ref={cvvInput} data-ifields-id="cvv-token" name="xCVV" type="hidden" />
            <span ref={cardError} data-ifields-id="card-data-error" className="sr-only" aria-hidden />
            <TextField label="Expiry (MM/YY)" value={exp} onChange={(e) => { setExp(e.target.value); clear('exp'); }} error={errs.exp} autoComplete="cc-exp" />
            <TextField label="Name on card" value={name} onChange={(e) => { setName(e.target.value); clear('name'); }} error={errs.name} autoComplete="cc-name" className="sm:col-span-2" />
            <TextField label="Billing address" value={street} onChange={(e) => { setStreet(e.target.value); clear('street'); }} error={errs.street} autoComplete="billing street-address" maxLength={100} className="sm:col-span-2" />
            <TextField label="ZIP / postal code" value={zip} onChange={(e) => { setZip(e.target.value); clear('zip'); }} error={errs.zip} autoComplete="billing postal-code" maxLength={10} />
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            <TextField label="Card number" inputMode="numeric" autoComplete="cc-number" value={num} onChange={(e) => { setNum(e.target.value); clear('num'); }} error={errs.num} className="sm:col-span-2" />
            <TextField label="Expiry (MM/YY)" value={exp} onChange={(e) => { setExp(e.target.value); clear('exp'); }} error={errs.exp} autoComplete="cc-exp" />
            <TextField label="CVV" inputMode="numeric" autoComplete="cc-csc" value={cvv} onChange={(e) => { setCvv(e.target.value.replace(/\D/g, '').slice(0, 4)); clear('cvv'); }} error={errs.cvv} />
            <TextField label="Name on card" value={name} onChange={(e) => { setName(e.target.value); clear('name'); }} error={errs.name} autoComplete="cc-name" className="sm:col-span-2" />
            <TextField label="Billing address" value={street} onChange={(e) => { setStreet(e.target.value); clear('street'); }} error={errs.street} autoComplete="billing street-address" maxLength={100} className="sm:col-span-2" />
            <TextField label="ZIP / postal code" value={zip} onChange={(e) => { setZip(e.target.value); clear('zip'); }} error={errs.zip} autoComplete="billing postal-code" maxLength={10} />
          </div>
        )}
        <div className="flex flex-wrap items-center justify-between gap-2 text-[12px] text-muted">
          <span>256-bit TLS encryption</span>
          <CardMarks />
        </div>
        <p className="text-[12px] text-muted">
          {unnamed ? 'Your card details are turned into a one-time token in your browser; our servers never receive the card number.'
            : gateway === 'cardknox'
            ? `This page never sees your card number or CVV — all card data is tokenized directly by ${LABEL[gateway]}.`
            : `Your card is turned into a one-time token by ${LABEL[gateway]} in your browser; our servers never receive the card number.`}
        </p>
      </div>
    );
  },
);

export function luhn(d: string): boolean {
  let sum = 0;
  for (let i = 0; i < d.length; i++) {
    let n = Number(d[d.length - 1 - i]);
    if (i % 2 === 1) {
      n *= 2;
      if (n > 9) n -= 9;
    }
    sum += n;
  }
  return d.length > 0 && sum % 10 === 0;
}
